import { Parcel } from '~api/som';

import { ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button, Card, FormControl, FormItem, Modal, TextInput } from '@ingrid/luterkort-react';

import { DimensionFields } from '~src/components/DimensionFields/DimensionFields';
import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { NumberInput } from '~src/components/NumberInput/NumberInput';
import { useTranslation } from '~src/i18n/use-translation';

export type ParcelAddSchema = Pick<Parcel, 'free_text' | 'dimensions' | 'weight'>;

type Mode = 'add' | 'edit';

interface Props {
  onSubmit: (values: ParcelAddSchema) => void;
  defaultValues?: ParcelAddSchema;
  trigger: ReactNode;
  mode: Mode;
}

export function ParcelFormDialog({ mode, defaultValues, onSubmit: onSubmitProp, trigger }: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  const { register, handleSubmit, formState, reset } = useForm<ParcelAddSchema>({
    defaultValues: {
      free_text: defaultValues?.free_text ?? '',
      weight: defaultValues?.weight ?? 0,
      dimensions: {
        height: defaultValues?.dimensions?.height ?? 0,
        length: defaultValues?.dimensions?.length ?? 0,
        width: defaultValues?.dimensions?.width ?? 0,
      },
    },
  });

  const { t } = useTranslation();

  function onSubmit(values: ParcelAddSchema) {
    onSubmitProp(values);
    setModalOpen(false);

    if (mode === 'add') {
      reset();
    }
  }

  function handleOpenChange(isOpen: boolean) {
    setModalOpen(isOpen);

    if (!isOpen) {
      reset();
    }
  }

  return (
    <FormControl.Root>
      <Modal.Root onOpenChange={handleOpenChange} open={modalOpen}>
        <Modal.Trigger>{trigger}</Modal.Trigger>

        <Modal.Content>
          <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />
          <Modal.Header title={t(mode === 'add' ? 'common.addParcel' : 'common.updateParcel')} />

          <Modal.Body>
            <Card.List css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)' }}>
              <FormItem.Root css={{ gridColumn: 'span 9' }}>
                <FormItem.Label>{t('common.freeText')}</FormItem.Label>
                <FormItem.Control>
                  <TextInput {...register('free_text')} />
                </FormItem.Control>
              </FormItem.Root>

              <FormItem.Root css={{ gridColumn: 'span 3' }}>
                <FormItem.Label>{t('common.weight')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.weight}>
                  <NumberInput
                    {...register('weight', { valueAsNumber: true, min: 0 })}
                    data-invalid={!!formState.errors.weight}
                    decimals={false}
                    suffix="g"
                  />
                </FormItem.Control>
              </FormItem.Root>

              <div style={{ display: 'flex', gap: '4px', gridColumn: 'span 12' }}>
                <DimensionFields register={register} formState={formState} />
              </div>
            </Card.List>
          </Modal.Body>

          <Modal.Footer>
            <Modal.CloseButton>
              <Button variant="secondary">{t('common.cancel')}</Button>
            </Modal.CloseButton>

            <FormControl.SubmitButton asChild>
              <Button>{t(mode === 'add' ? 'common.addParcel' : 'common.updateParcel')}</Button>
            </FormControl.SubmitButton>
          </Modal.Footer>

          <FormLeaveBlocker formState={formState} />
        </Modal.Content>
      </Modal.Root>
    </FormControl.Root>
  );
}
